/**
 * `useMountedRef` — the shared implementation of the is-this-still-mounted guard.
 *
 * Adopted by `ActionBar`, `RecipeSheet`, `RecordForm`,
 * `ScheduledJobDetailDrawer`, and (DW-454) `Composer`, `ContinueInChatButton`,
 * `TodayBriefing`, `SessionPointer`, `DashboardBoardView`,
 * `usePendingWidgetRemoval`, `useStackBulkCancel` and
 * `useReportConversationRead`. The one site that deliberately kept its own
 * `isMountedRef` — the N8N `useChat` hook — was deleted in T-20260919-4, so
 * this is the shared implementation for every mount guard. A new hand-rolled
 * copy is a lint error: `MOUNT_GUARD_RESTRICTION` in `eslint.config.js` bans a
 * `useRef(...)` declarator named like a mount flag (`mounted`, `isMounted`,
 * `mountedRef`, `alive`, `aliveRef`, …) everywhere except this file and the
 * StrictMode capability suite (pinned by
 * `scripts/__tests__/eslint-mount-guard-rule.test.ts`).
 *
 * WHY THE RE-ARM EXISTS (T-20260830-1 / DW-376). `src/main.tsx` wraps the app in
 * `<StrictMode>`, which runs mount → cleanup → mount on the SAME component
 * instance. A ref that starts `true` and is only ever set `false` by a cleanup
 * is therefore permanently `false` from the first dev mount onward, and every
 * `if (!mountedRef.current) return;` keyed on it silently suppresses real work —
 * the component renders fine and does nothing. `ActionBar` shipped exactly that:
 * Approve / Iterate / Decline resolved their PATCH, hit the guard, and dropped
 * the state transition, leaving the bar stuck at `data-state="pending"` forever.
 *
 * The fault is NOT development-only in principle. A ref that never re-arms is
 * equally wrong on any genuine remount of the same instance; StrictMode just
 * guarantees one on every mount instead of leaving it to chance.
 *
 * 🔴 THE RE-ARM MUST STAY INSIDE THIS MOUNTING EFFECT, with `[]` deps. Moving
 * it anywhere that also runs after a real teardown (an every-render effect, a
 * render body) would leave the flag `true` forever and reintroduce precisely the
 * setState-after-unmount defect the guard exists to prevent — while looking
 * correct in every StrictMode test.
 *
 * Pinned by `src/lib/hooks/__tests__/useMountedRef.test.tsx`. The original
 * adopters (ActionBar, RecipeSheet, RecordForm, ScheduledJobDetailDrawer) and
 * Composer also each have their own StrictMode behaviour case; the other
 * DW-454 adopters rely on the hook's own suite. The double-invoke itself is
 * pinned as a capability in `src/__tests__/strict-mode-double-invoke.test.tsx`.
 */

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

export function useMountedRef(): RefObject<boolean> {
  const mountedRef = useRef(true);

  useEffect(() => {
    // Re-arm on EVERY mount — see the header. StrictMode's cleanup already set
    // this false once before this line runs on the second mount.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return mountedRef;
}
